import {
  AvailableSeasons,
  GameWeek,
  NFL_TEAMS_TOTAL,
  Season,
  TeamAbrvToTeamID,
  TeamNamesByID,
  TeamRivalries,
} from '@shared/constants';
import {
  breakByeWeekID,
  breakGameID,
  breakUID,
  toByeWeekID,
  toNumber,
  toSeasonWeekID,
  toTeamAbrvID,
  toUID,
  toWeekID,
} from '@shared/helpers';
import {
  AllSeasonScheduleByTeamType,
  BYE,
  ByeWeekID,
  DecomposedUID,
  GameID,
  IEnhancedTeamStats,
  IGame,
  IGameStats,
  IMatchup,
  IScoringPlay,
  ITeamDetails,
  ITeamsOnByeWeek,
  SeasonID,
  SeasonWeekID,
  TeamAbrvID,
  TeamAbrvType,
  TeamID,
  UID,
  WeekID,
} from '@shared/types';
import {
  ByeWeeksInfo,
  CategoryName,
  CategoryNameT,
  IRanking,
  IRankingCategories,
  ITeamWeekRank,
  IWeekRank,
  Maybe,
  TeamColorsType,
  TeamsOnByeWeek,
} from 'types';
import { sortByTeamID, sortTeamsByValue } from './SortHelpers';
import { getTeamIDsFromGameID } from './StatsHelper';

export const TID_LIST = [
  '0001-ARI',
  '0002-ATL',
  '0003-BAL',
  '0004-BUF',
  '0005-CAR',
  '0006-CHI',
  '0007-CIN',
  '0008-CLE',
  '0009-DAL',
  '0010-DEN',
  '0011-DET',
  '0012-GB',
  '0013-HOU',
  '0014-IND',
  '0015-JAX',
  '0016-KC',
  '0017-LV',
  '0018-LAC',
  '0019-LAR',
  '0020-MIA',
  '0021-MIN',
  '0022-NE',
  '0023-NO',
  '0024-NYG',
  '0025-NYJ',
  '0026-PIT',
  '0027-PHI',
  '0028-SF',
  '0029-SEA',
  '0030-TB',
  '0031-TEN',
  '0032-WSH',
] as const;

// export const TeamNamesByID: string[] = [
//   'All',
//   'ARI',
//   'ATL',
//   'BAL',
//   'BUF',
//   'CAR',
//   'CHI',
//   'CIN',
//   'CLE',
//   'DAL',
//   'DEN',
//   'DET',
//   'GB',
//   'HOU',
//   'IND',
//   'JAX',
//   'KC',
//   'LV',
//   'LAC',
//   'LAR',
//   'MIA',
//   'MIN',
//   'NE',
//   'NO',
//   'NYG',
//   'NYJ',
//   'PIT',
//   'PHI',
//   'SF',
//   'SEA',
//   'TB',
//   'TEN',
//   'WSH',
// ];

// enum TeamAbrvToTeamID {
//   ARI = '1',
//   ATL = '2',
//   BAL = '3',
//   BUF = '4',
//   CAR = '5',
//   CHI = '6',
//   CIN = '7',
//   CLE = '8',
//   DAL = '9',
//   DEN = '10',
//   DET = '11',
//   GB = '12',
//   HOU = '13',
//   IND = '14',
//   JAX = '15',
//   KC = '16',
//   LV = '17',
//   LAC = '18',
//   LAR = '19',
//   MIA = '20',
//   MIN = '21',
//   NE = '22',
//   NO = '23',
//   NYG = '24',
//   NYJ = '25',
//   PIT = '26',
//   PHI = '27',
//   SF = '28',
//   SEA = '29',
//   TB = '30',
//   TEN = '31',
//   WSH = '32',
// }

export const TeamColor = {
  [TeamAbrvToTeamID.ARI]: '#97233F',
  [TeamAbrvToTeamID.ATL]: '#A71930',
  [TeamAbrvToTeamID.BAL]: '#241773',
  [TeamAbrvToTeamID.BUF]: '#00338D',
  [TeamAbrvToTeamID.CAR]: '#0085CA',
  [TeamAbrvToTeamID.CHI]: '#0B162A',
  [TeamAbrvToTeamID.CIN]: '#FB4F14',
  [TeamAbrvToTeamID.CLE]: '#311D00',
  [TeamAbrvToTeamID.DAL]: '#041E42',
  [TeamAbrvToTeamID.DEN]: '#002244',
  [TeamAbrvToTeamID.DET]: '#0076B6',
  [TeamAbrvToTeamID.GB]: '#203731',
  [TeamAbrvToTeamID.HOU]: '#03202F',
  [TeamAbrvToTeamID.IND]: '#002C5F',
  [TeamAbrvToTeamID.JAX]: '#006778',
  [TeamAbrvToTeamID.KC]: '#E31837',
  [TeamAbrvToTeamID.LAC]: '#0080C6',
  [TeamAbrvToTeamID.LAR]: '#003594',
  [TeamAbrvToTeamID.LV]: '#000000',
  [TeamAbrvToTeamID.MIA]: '#008E97',
  [TeamAbrvToTeamID.MIN]: '#4F2683',
  [TeamAbrvToTeamID.NE]: '#002244',
  [TeamAbrvToTeamID.NO]: '#D3BC8D',
  [TeamAbrvToTeamID.NYG]: '#0B2265',
  [TeamAbrvToTeamID.NYJ]: '#125740',
  [TeamAbrvToTeamID.PHI]: '#004C54',
  [TeamAbrvToTeamID.PIT]: '#FFB612',
  [TeamAbrvToTeamID.SEA]: '#002244',
  [TeamAbrvToTeamID.SF]: '#AA0000',
  [TeamAbrvToTeamID.TB]: '#D50A0A',
  [TeamAbrvToTeamID.TEN]: '#4B92DB',
  [TeamAbrvToTeamID.WSH]: '#773141',
};

export const TeamSecondaryColor = {
  [TeamAbrvToTeamID.ARI]: '#000000',
  [TeamAbrvToTeamID.ATL]: '#A5ACAF',
  [TeamAbrvToTeamID.BAL]: '#9E7C0C',
  [TeamAbrvToTeamID.BUF]: '#C60C30',
  [TeamAbrvToTeamID.CAR]: '#101820',
  [TeamAbrvToTeamID.CHI]: '#C83803',
  [TeamAbrvToTeamID.CIN]: '#000000',
  [TeamAbrvToTeamID.CLE]: '#FF3C00',
  [TeamAbrvToTeamID.DAL]: '#869397',
  [TeamAbrvToTeamID.DEN]: '#FB4F14',
  [TeamAbrvToTeamID.DET]: '#B0B7BC',
  [TeamAbrvToTeamID.GB]: '#FFB612',
  [TeamAbrvToTeamID.HOU]: '#A71930',
  [TeamAbrvToTeamID.IND]: '#A5ACAF',
  [TeamAbrvToTeamID.JAX]: '#D7A22A',
  [TeamAbrvToTeamID.KC]: '#FFB81C',
  [TeamAbrvToTeamID.LAC]: '#FFC20E',
  [TeamAbrvToTeamID.LAR]: '#FFA300',
  [TeamAbrvToTeamID.LV]: '#A5ACAF',
  [TeamAbrvToTeamID.MIA]: '#FC4C02',
  [TeamAbrvToTeamID.MIN]: '#FFC62F',
  [TeamAbrvToTeamID.NE]: '#C60C30',
  [TeamAbrvToTeamID.NO]: '#101820',
  [TeamAbrvToTeamID.NYG]: '#A71930',
  [TeamAbrvToTeamID.NYJ]: '#000000',
  [TeamAbrvToTeamID.PHI]: '#A5ACAF',
  [TeamAbrvToTeamID.PIT]: '#101820',
  [TeamAbrvToTeamID.SEA]: '#69BE28',
  [TeamAbrvToTeamID.SF]: '#B3995D',
  [TeamAbrvToTeamID.TB]: '#FF7900',
  [TeamAbrvToTeamID.TEN]: '#0C2340',
  [TeamAbrvToTeamID.WSH]: '#FFB612',
};

export const USStatesStartingWithI: string[] = ['Idaho', 'Illinois', 'Indiana', 'Iowa'];

export const USStatesWithNFLTeams: string[] = [
  'Arizona',
  'Georgia',
  'Maryland',
  'New York',
  'North Carolina',
  'Illinois',
  'Ohio',
  'Texas',
  'Colorado',
  'Michigan',
  'Wisconsin',
  'Indiana',
  'Florida',
  'Missouri',
  'Nevada',
  'California',
  'Minnesota',
  'Massachusetts',
  'Louisiana',
  'New Jersey',
  'Pennsylvania',
  'Washington',
  'Tennessee',
];

export const TeamDivisionMap: { [key in TeamAbrvType]: string } = {
  ARI: 'NFC West',
  ATL: 'NFC South',
  BAL: 'AFC North',
  BUF: 'AFC East',
  CAR: 'NFC South',
  CHI: 'NFC North',
  CIN: 'AFC North',
  CLE: 'AFC North',
  DAL: 'NFC East',
  DEN: 'AFC West',
  DET: 'NFC North',
  GB: 'NFC North',
  HOU: 'AFC South',
  IND: 'AFC South',
  JAX: 'AFC South',
  KC: 'AFC West',
  LV: 'AFC West',
  LAC: 'AFC West',
  LAR: 'NFC West',
  MIA: 'AFC East',
  MIN: 'NFC North',
  NE: 'AFC East',
  NO: 'NFC South',
  NYG: 'NFC East',
  NYJ: 'AFC East',
  PIT: 'AFC North',
  PHI: 'NFC East',
  SF: 'NFC West',
  SEA: 'NFC West',
  TB: 'NFC South',
  TEN: 'AFC South',
  WSH: 'NFC East',
};

export const TeamConferenceMap: { [key in TeamAbrvType]: string } = {
  BAL: 'AFC',
  BUF: 'AFC',
  CIN: 'AFC',
  CLE: 'AFC',
  DEN: 'AFC',
  HOU: 'AFC',
  IND: 'AFC',
  JAX: 'AFC',
  KC: 'AFC',
  LAC: 'AFC',
  LV: 'AFC',
  MIA: 'AFC',
  NE: 'AFC',
  NYJ: 'AFC',
  PIT: 'AFC',
  TEN: 'AFC',
  ARI: 'NFC',
  ATL: 'NFC',
  CAR: 'NFC',
  CHI: 'NFC',
  DAL: 'NFC',
  DET: 'NFC',
  GB: 'NFC',
  LAR: 'NFC',
  MIN: 'NFC',
  NO: 'NFC',
  NYG: 'NFC',
  PHI: 'NFC',
  SEA: 'NFC',
  SF: 'NFC',
  TB: 'NFC',
  WSH: 'NFC',
};

/**
 * Determines if a game is a conference matchup (both teams in same conference).
 *
 * @param {GameID} gameID - The game identifier
 *
 * @return {boolean} - True if both teams are in the same conference, false otherwise
 *
 * @example
 * isConferenceMatch('20240905_BAL@KC');  // true (both AFC)
 * isConferenceMatch('20240908_PHI@GB');  // false (NFC vs NFC)
 */
export const isConferenceMatch = (gameID: GameID) => {
  const { awayTeamAbrv, homeTeamAbrv } = breakGameID(gameID);
  return TeamConferenceMap[awayTeamAbrv] === TeamConferenceMap[homeTeamAbrv];
};

/**
 * Determines if a game is a division matchup (both teams in same division).
 *
 * @param {GameID} gameID - The game identifier
 *
 * @return {boolean} - True if both teams are in the same division, false otherwise
 *
 * @example
 * isDivisionMatch('20240905_BAL@CIN');  // true (both AFC North)
 * isDivisionMatch('20240908_BAL@KC');   // false (different divisions)
 */
export const isDivisionMatch = (gameID: GameID) => {
  const { awayTeamAbrv, homeTeamAbrv } = breakGameID(gameID);
  return TeamDivisionMap[awayTeamAbrv] === TeamDivisionMap[homeTeamAbrv];
};

// #endregion

/**
 * Creates an empty/initialized team stats object with all statistical categories set to zero.
 *
 * @description
 * Generates a complete IEnhancedTeamStats object with default values for all offensive,
 * defensive, and special teams statistics. Used as a template or placeholder when actual
 * stats are not yet available.
 *
 * @param {TeamID} teamID - The team identifier (1-32)
 * @param {WeekID} weekID - The week identifier
 * @param {GameID} gameID - The game identifier
 *
 * @return {IEnhancedTeamStats} - Complete team stats object with all values initialized to zero
 *
 * @example
 * const emptyStats = getNflSeasonTeamStats('11', 'Week1', '20240905_DET@KC');
 * // Returns fully initialized stats object with uID, seasonWeekID, and all stats at 0
 */
export const getNflSeasonTeamStats = (teamID: TeamID, weekID: WeekID, gameID: GameID): IEnhancedTeamStats => {
  const uID = toUID(weekID, teamID, gameID);
  const seasonWeekID = toSeasonWeekID(breakUID(uID).yearID, weekID);
  return {
    uID,
    gameID,
    weekID,
    teamID,
    seasonWeekID,
    snapCounts: {
      totalDefensive: '0',
      totalOffensive: '0',
      totalSpecialTeams: '0',
    },
    team: TeamNamesByID[parseInt(teamID)],
    teamAbv: TeamNamesByID[parseInt(teamID)],
    defensiveInterceptions: '0',
    defensiveOrSpecialTeamsTds: '0',
    defTD: '0',
    firstDowns: '0',
    firstDownsFromPenalties: '0',
    fourthDownAttemptsAgainst: '0',
    fourthDownEfficiency: '0',
    fourthDownStopEfficiency: '0',
    fourthDownStops: '0',
    fumblesLost: '0',
    fumblesRecovered: '0',
    interceptionsThrown: '0',
    passAttemptsAgainst: '0',
    passCompletionsAndAttempts: '0-0',
    passIncompletePercentage: '0',
    passIncompletions: '0',
    passingFirstDowns: '0',
    passingYards: '0',
    passingYardsAllowed: '0',
    penalties: '0',
    pointsScored: 0,
    possession: '0',
    ptsAllowed: '0',
    redZoneAttemptsAgainst: '0',
    redZoneScoredAndAttempted: '0-0',
    redZoneStopEfficiency: '0',
    redZoneStops: '0',
    rushingAttempts: '0',
    rushingFirstDowns: '0',
    rushingYards: '0',
    rushingYardsAllowed: '0',
    sacks: '0',
    sacksAndYardsLost: '0-0',
    safeties: '0',
    scoring2PtAgainst: 0,
    scoring2PtFor: 0,
    scoringEPAgainst: 0,
    scoringEPFor: 0,
    scoringFGAgainst: 0,
    scoringFGFor: 0,
    scoringPassingTDAgainst: 0,
    scoringPassingTDFor: 0,
    scoringRushingTDAgainst: 0,
    scoringRushingTDFor: 0,
    scoringTotalTDAgainst: 0,
    scoringTotalTDFor: 0,
    thirdDownAttemptsAgainst: '0',
    thirdDownEfficiency: '0-0',
    thirdDownStopEfficiency: '0',
    thirdDownStops: '0',
    totalDrives: '0',
    totalPlays: '0',
    totalYards: '0',
    turnovers: '0',
    yardsPerPass: '0',
    yardsPerPlay: '0',
    yardsPerRush: '0',
    ydsAllowed: '0',
  };
};

/**
 * Retrieves primary and secondary colors for a team by numeric team ID.
 *
 * @param {number} teamId - The numeric team identifier (1-32)
 *
 * @return {TeamColorsType} - Object containing primary and secondary hex color codes
 *
 * @example
 * getTeamColorsByTeamId(11);  // { primary: '#0076B6', secondary: '#B0B7BC' } (Lions)
 */
export const getTeamColorsByTeamId = (teamId: number): TeamColorsType => {
  const teamAbr = TeamNamesByID[teamId];
  const key = TeamAbrvToTeamID[teamAbr as TeamAbrvType];

  const primaryColor = TeamColor[key];
  const secondaryColor = TeamSecondaryColor[key];

  return {
    primary: primaryColor,
    secondary: secondaryColor,
  };
};

/**
 * Creates a prefix for the ordered UID with leading zeros for weekID and teamID.
 *
 * @param weekID
 * @param teamID
 * @returns
 */
export const getPrefixForOrderedUID = (weekID: WeekID, teamID: TeamID): string => {
  const weekIDNum = toNumber(weekID);
  const OrderedWeekID = `${weekIDNum}`.padStart(2, '0');
  const OrderedTeamID = `${teamID}`.padStart(2, '0');
  return `${OrderedWeekID}-${OrderedTeamID}`;
};

/**
 * Converts a team ID to 4-character zero-padded format.
 *
 * @param {TeamID} teamID - The team identifier to pad
 *
 * @return {string} - Zero-padded 4-character team ID
 *
 * @example
 * to4CharTeamID('11');  // '0011'
 * to4CharTeamID('1');   // '0001'
 */
export const to4CharTeamID = (teamID: TeamID) => teamID.padStart(4, '0');

/**
 * Converts a 4-character zero-padded team ID back to numeric string.
 *
 * @param {TeamID} paddedTeamID - The zero-padded team identifier
 *
 * @return {string} - Numeric team ID without padding
 *
 * @example
 * break4CharTeamID('0011');  // '11'
 * break4CharTeamID('0001');  // '1'
 */
export const break4CharTeamID = (paddedTeamID: TeamID) => String(parseInt(paddedTeamID));

/**
 * Extracts home and away team information from a game ID.
 *
 * @param {GameID} gameID - The game identifier in format YYYYMMDD_AWAY@HOME
 *
 * @return {Object} Object containing homeTeamID, awayTeamID, homeAbrv, and awayAbrv properties
 *
 * @example
 * getHomeAndAwayTeamsByGameID('20240905_BAL@KC');
 * // { homeTeamID: '16', awayTeamID: '3', homeAbrv: 'KC', awayAbrv: 'BAL' }
 */
export const getHomeAndAwayTeamsByGameID = (
  gameID: GameID
): { homeTeamID: TeamID; awayTeamID: TeamID; homeAbrv: TeamAbrvType; awayAbrv: TeamAbrvType } => {
  const [awayTeamAbrv, homeTeamAbrv] = gameID.split('_')[1].split('@') as TeamAbrvType[];
  const homeTeamID = TeamAbrvToTeamID[homeTeamAbrv as keyof typeof TeamAbrvToTeamID];
  const awayTeamID = TeamAbrvToTeamID[awayTeamAbrv as keyof typeof TeamAbrvToTeamID];
  return { homeTeamID, awayTeamID, homeAbrv: homeTeamAbrv, awayAbrv: awayTeamAbrv };
};
// #endregion

// #region TEAM BYE WEEK HELPERS ********************************************************

/**
 * Calculates which teams are on bye for a given week based on the scheduled games.
 *
 * @description
 * Determines teams on bye week by identifying which teams are NOT playing in the
 * provided games list. All 32 teams minus teams with games equals teams on bye.
 *
 * @param {WeekID} weekID - The week identifier
 * @param {Array<IGame | IMatchup>} games - List of games scheduled for the week
 * @param {SeasonID} [year='2025'] - The season year
 *
 * @return {ITeamsOnByeWeek} - Object containing seasonWeekID, teamIDs on bye, weekID, and year
 *
 * @example
 * calculateTeamsOnByeWeek('Week5', gamesForWeek5, '2024');
 * // { seasonWeekID: '2024-05', teamIDs: ['11', '12'], weekID: 'Week5', year: '2024' }
 */
export const calculateTeamsOnByeWeek = (
  weekID: WeekID,
  games: (IGame | IMatchup)[],
  year: SeasonID = '2025'
): ITeamsOnByeWeek => {
  if (!weekID) return { seasonWeekID: `${year}-01`, teamIDs: [], weekID: GameWeek.Week1, year };

  const seasonWeekID = toSeasonWeekID(year, weekID);
  if (games.length === 0) return { seasonWeekID, teamIDs: [], weekID: weekID as GameWeek, year };

  const teamsNotOnBye: TeamID[] = [];
  games.forEach((game) => {
    const [homeTeam, awayTeam] = getTeamIDsFromGameID(game.gameID);
    teamsNotOnBye.push(homeTeam, awayTeam);
  });

  const teamNames: TeamID[] = Object.values(TeamAbrvToTeamID).filter((team) => !teamsNotOnBye.includes(team));
  return { seasonWeekID, teamIDs: teamNames, weekID: weekID as GameWeek, year };
};

const getTeamsOnByeWeekInit = (week: GameWeek, year: SeasonID): ITeamsOnByeWeek => ({
  seasonWeekID: toSeasonWeekID(year, week),
  weekID: week,
  teamIDs: [],
  year,
});

/**
 * Creates initial state object for tracking teams on bye week for entire season.
 *
 * @description
 * Generates a complete season structure with all 18 weeks initialized with empty
 * team lists. Used as initial Redux state or for resetting bye week tracking.
 *
 * @param {SeasonID} [year='2025'] - The season year
 *
 * @return {TeamsOnByeWeek} - Map of all weeks with empty bye week information
 *
 * @example
 * const initialState = getTeamsOnByeWeekInitialState('2024');
 * // { Week1: { weekID: 'Week1', teamIDs: [], ... }, Week2: { ... }, ... }
 */
export const getTeamsOnByeWeekInitialState = (year: SeasonID = '2025'): TeamsOnByeWeek => {
  const weekKeys = Object.keys(GameWeek) as GameWeek[];
  const teamsOnByeWeek: ByeWeeksInfo[] = [];

  weekKeys.forEach((week) => {
    teamsOnByeWeek.push({
      seasonYear: year,
      weekID: week,
      teamsOnByeWeek: getTeamsOnByeWeekInit(week, year),
      byeWeekTeamsAddedToSeason: getTeamsOnByeWeekInit(week, year),
    });
  });

  const byeWeekTeams: TeamsOnByeWeek = weekKeys.reduce((acc, week, index) => {
    acc[week] = teamsOnByeWeek[index];
    return acc;
  }, {} as TeamsOnByeWeek);

  return byeWeekTeams;
};

/**
 * Extracts bye week information for all teams in a given season.
 *
 * @description
 * Processes team details to generate a list of bye week assignments for each team.
 * Groups teams by their bye week and returns structured bye week information.
 *
 * @param {ITeamDetails[]} teams - Array of team details including bye week info
 * @param {SeasonID} year - The season year
 *
 * @return {ITeamsOnByeWeek[]} - Array of bye week objects, one per week with teams on bye
 *
 * @example
 * const byeWeeks = getSeasonTeamsOnByeWeek(allTeams, '2024');
 * // [{ seasonWeekID: '2024-05', teamIDs: ['11', '12'], weekID: 'Week5', year: '2024' }, ...]
 */
export const getSeasonTeamsOnByeWeek = (teams: ITeamDetails[], year: SeasonID): ITeamsOnByeWeek[] => {
  const weekIDs = Object.keys(GameWeek) as GameWeek[];
  const byeWeeks = {} as { [key: WeekID]: ITeamsOnByeWeek };

  weekIDs.forEach((weekID) => {
    const seasonWeekID = toSeasonWeekID(year, weekID);
    byeWeeks[weekID] = { seasonWeekID, weekID, teamIDs: [], year };
  });

  teams.forEach((team) => {
    const uID = team.scheduleByYear[year].find((uid) => breakUID(uid).gameID === BYE);
    if (uID) {
      const { weekID, teamID } = breakUID(uID);
      byeWeeks[weekID].teamIDs.push(teamID);
    }
  });

  const byeWeekTeams = Object.values(byeWeeks) as ITeamsOnByeWeek[];
  console.log('byeWeekTeams', byeWeekTeams);

  return byeWeekTeams;
};

/**
 * Retrieves bye week information for all teams across all available seasons.
 *
 * @description
 * Aggregates bye week data from all seasons defined in AvailableSeasons constant.
 * Useful for loading historical bye week data or initializing multi-season views.
 *
 * @param {ITeamDetails[]} teams - Array of team details with multi-season bye week info
 *
 * @return {ITeamsOnByeWeek[]} - Combined array of bye weeks across all seasons
 *
 * @example
 * const allByeWeeks = getAllTeamsAllAvailableSeasonsByeWeeks(teamDetails);
 * // Returns bye weeks for 2023, 2024, 2025 seasons combined
 */
export const getAllTeamsAllAvailableSeasonsByeWeeks = (teams: ITeamDetails[]): ITeamsOnByeWeek[] => {
  const allByeWeeks: ITeamsOnByeWeek[] = [];
  AvailableSeasons.forEach((seasonID: SeasonID) => allByeWeeks.push(...getSeasonTeamsOnByeWeek(teams, seasonID)));
  return allByeWeeks;
};

/**
 * Inserts bye week entries into each team's season schedule.
 *
 * @description
 * Processes all team schedules and adds BYE entries in appropriate weeks. Detects
 * bye weeks by identifying gaps in the schedule and inserts BYE game entries to
 * maintain complete 18-week schedules for all teams.
 *
 * @param {AllSeasonScheduleByTeamType} allTeamsOrderedUID - Map of team schedules by UID
 *
 * @return {AllSeasonScheduleByTeamType} - Updated schedules with bye weeks inserted
 *
 * @example
 * const schedulesWithByes = setByeWeeksInTeamSeasonSchedule(teamSchedules);
 * // Each team now has 18 entries including BYE weeks
 */
export const setByeWeeksInTeamSeasonSchedule = (
  allTeamsOrderedUID: AllSeasonScheduleByTeamType
): AllSeasonScheduleByTeamType => {
  const WEEK_ID_OFFSET_START = 5;
  const WEEK_ID_OFFSET_END = 7;
  const byeWeeks: UID[] = [];
  const teamsKeys = Object.keys(allTeamsOrderedUID);
  const allTeamsScheduleWithByeWeeks: AllSeasonScheduleByTeamType = {};
  teamsKeys.sort();

  teamsKeys.forEach((key, index) => {
    const gamesList = allTeamsOrderedUID[key];
    const teamID = `${index + 1}` as TeamID;

    const insertIndex = gamesList.findIndex(
      (value: UID, index) => index + 1 !== parseInt(value.slice(WEEK_ID_OFFSET_START, WEEK_ID_OFFSET_END))
    );
    const weekID = toWeekID(insertIndex + 1);
    // '11-01-Week11-1-ARI-BYE'
    const byeWeekValue = toUID(weekID, teamID, 'BYE', Season.Season2025);
    byeWeeks.push(byeWeekValue);
    gamesList.splice(insertIndex, 0, byeWeekValue);
    allTeamsScheduleWithByeWeeks[key] = [...gamesList];
  });

  return allTeamsScheduleWithByeWeeks;
};
// #endregion

// #region TEAM STATS HELPERS ***********************************************************
type OpponentAndGameInfo = {
  weekID?: WeekID;
  teamID?: TeamID;
  teamAbrv?: string;
  gameID?: GameID;
  isByeWeek?: boolean;
};

export const getOpponentAndGameInfo = (category: ITeamWeekRank, schedule: UID[]): OpponentAndGameInfo => {
  const categoryWeekID = category.week as GameWeek;
  const opponentUID = schedule.find((uID) => breakUID(uID).weekID === categoryWeekID);
  if (!opponentUID) {
    console.log(`No opponent found for week ${categoryWeekID}`);
    return {};
  }
  return getOpponentInfo(opponentUID);
};

export const getOpponentInfo = (opponentUID: UID): OpponentAndGameInfo => {
  const { weekID, teamID, teamAbrv, gameID } = breakUID(opponentUID) as DecomposedUID;
  const isByeWeek = gameID === BYE;

  return {
    weekID,
    teamID,
    teamAbrv,
    gameID,
    isByeWeek,
  };
};

/**
 * Extracts detailed scoring statistics from game scoring plays for a specific team.
 *
 * @description
 * Processes all scoring plays in a game and categorizes them by type (passing TD,
 * rushing TD, FG, extra point, 2-pt conversion) for both offensive (For) and
 * defensive (Against) perspectives.
 *
 * @param {IScoringPlay[]} scoringPlays - Array of all scoring plays in the game
 * @param {TeamID} teamID - The team identifier to extract stats for
 * @param {string} teamName - The team name/abbreviation
 *
 * @return {Object} - Object containing 12 IRanking objects for different scoring categories:
 *   - scoringPassingTDFor/Against, scoringRushingTDFor/Against
 *   - scoringFGFor/Against, scoringEPFor/Against
 *   - scoring2PtFor/Against, scoringTotalTDsFor/Against
 *
 * @example
 * const stats = extractGameScoringStats(plays, '11', 'DET');
 * // Returns detailed breakdown of all scoring by category
 */
export const extractGameScoringStats = (scoringPlays: IScoringPlay[], teamID: TeamID, teamName: string) => {
  const scoringPassingTDAgainst: IRanking = { teamID, teamName, value: 0 };
  const scoringRushingTDAgainst: IRanking = { teamID, teamName, value: 0 };
  const scoringFGAgainst: IRanking = { teamID, teamName, value: 0 };
  const scoringEPAgainst: IRanking = { teamID, teamName, value: 0 };
  const scoring2PtAgainst: IRanking = { teamID, teamName, value: 0 };
  const scoringPassingTDFor: IRanking = { teamID, teamName, value: 0 };
  const scoringRushingTDFor: IRanking = { teamID, teamName, value: 0 };
  const scoringFGFor: IRanking = { teamID, teamName, value: 0 };
  const scoringEPFor: IRanking = { teamID, teamName, value: 0 };
  const scoring2PtFor: IRanking = { teamID, teamName, value: 0 };
  const scoringTotalTDsFor: IRanking = { teamID, teamName, value: 0 };
  const scoringTotalTDsAgainst: IRanking = { teamID, teamName, value: 0 };

  scoringPlays.forEach((play: IScoringPlay) => {
    if (play.scoreType === 'TD') {
      if (play.teamID === teamID) {
        scoringPassingTDFor.value += play.score.indexOf('pass') > -1 ? 1 : 0;
        scoringRushingTDFor.value += play.score.indexOf('Run') > -1 ? 1 : 0;
        scoringRushingTDFor.value += play.score.indexOf('Rush') > -1 ? 1 : 0;
        scoringEPFor.value += play.score.indexOf('Kick)') > -1 ? 1 : 0;
        scoring2PtFor.value += play.score.indexOf('Two-Point Conversion)') > -1 ? 1 : 0;
        scoringTotalTDsFor.value = scoringRushingTDFor.value + scoringPassingTDFor.value;
      } else {
        scoringPassingTDAgainst.value += play.score.indexOf('pass') > -1 ? 1 : 0;
        scoringRushingTDAgainst.value += play.score.indexOf('Run') > -1 ? 1 : 0;
        scoringRushingTDAgainst.value += play.score.indexOf('Rush') > -1 ? 1 : 0;
        scoringEPAgainst.value += play.score.indexOf('Kick)') > -1 ? 1 : 0;
        scoring2PtAgainst.value += play.score.indexOf('Two-Point Conversion)') > -1 ? 1 : 0;
        scoringTotalTDsAgainst.value = scoringRushingTDAgainst.value + scoringPassingTDAgainst.value;
      }
    } else if (play.scoreType === 'FG') {
      if (play.teamID === teamID) {
        scoringFGFor.value += 1;
      } else {
        scoringFGAgainst.value += 1;
      }
    }
  });

  return {
    scoringPassingTDAgainst,
    scoringRushingTDAgainst,
    scoringFGAgainst,
    scoringEPAgainst,
    scoring2PtAgainst,
    scoringTotalTDsAgainst,
    scoringPassingTDFor,
    scoringRushingTDFor,
    scoringFGFor,
    scoringEPFor,
    scoring2PtFor,
    scoringTotalTDsFor,
  };
};

/**
 * Creates comprehensive weekly rankings for all NFL teams across all statistical categories.
 *
 * @description
 * Processes team statistics and game data to generate rankings in 66+ statistical
 * categories including offensive, defensive, efficiency, and special teams metrics.
 * Rankings are sorted with best performers first and include tied ranks.
 *
 * @param {IEnhancedTeamStats[]} teamStats - Array of enhanced team statistics for all teams
 * @param {GameWeek} weekID - The week identifier for these rankings
 * @param {IGameStats[]} gameStats - Array of game statistics for detailed scoring breakdown
 *
 * @return {IRankingCategories} - Complete ranking categories object with all 66+ categories
 *
 * @example
 * const rankings = createWeeklyRankingsForAllTeams(allTeamStats, 'Week5', gameResults);
 * // Returns object with totalYards, totalPassingYards, totalPointsScored, etc.
 */
export const createWeeklyRankingsForAllTeams = (
  teamStats: IEnhancedTeamStats[],
  weekID: GameWeek,
  gameStats: IGameStats[]
): IRankingCategories => {
  const teamStatsForSelectedWeek: IEnhancedTeamStats[] = teamStats.filter((team) => team.weekID === weekID);
  // OFFENSIVE RANKINGS
  const totalYards: IRanking[] = [];
  const totalPlays: IRanking[] = [];
  const totalYardsPerPlay: IRanking[] = [];
  const totalPassingYards: IRanking[] = [];
  const totalPassCompletions: IRanking[] = [];
  const totalPassAttempts: IRanking[] = [];
  const totalPassingPercentage: IRanking[] = [];
  const totalPassingYardsPerAttempt: IRanking[] = [];
  const totalRushingYards: IRanking[] = [];
  const totalRushingAttempts: IRanking[] = [];
  const totalRushingYardsPerAttempt: IRanking[] = [];
  const totalPoints: IRanking[] = [];
  const totalInterceptionsThrown: IRanking[] = [];
  const totalFumblesLost: IRanking[] = [];
  const totalTimeOfPossession: IRanking[] = [];
  const totalPenalties: IRanking[] = [];
  const totalPenaltyYards: IRanking[] = [];
  const totalScoringPassingTDFor: IRanking[] = [];
  const totalScoringRushingTDFor: IRanking[] = [];
  const totalScoringFGFor: IRanking[] = [];
  const totalScoringEPFor: IRanking[] = [];
  const totalScoring2PtFor: IRanking[] = [];
  const totalScoringTotalTDsFor: IRanking[] = [];

  const totalSackedTimes: IRanking[] = [];
  const totalSackedYardsLost: IRanking[] = [];
  const totalDrives: IRanking[] = [];
  const totalThirdDownAttempts: IRanking[] = [];
  const totalThirdDownConverted: IRanking[] = [];
  const totalThirdDownEfficiency: IRanking[] = [];
  const totalRedZoneScored: IRanking[] = [];
  const totalRedZoneAttempts: IRanking[] = [];
  const totalRedZoneEfficiency: IRanking[] = [];
  const totalFirstDowns: IRanking[] = [];
  const totalFirstDownsFromPenalties: IRanking[] = [];
  const totalFirstDownsFromRushing: IRanking[] = [];
  const totalFirstDownsFromPassing: IRanking[] = [];
  const totalFourthDownAttempts: IRanking[] = [];
  const totalFourthDownScored: IRanking[] = [];
  const totalFourthDownEfficiency: IRanking[] = [];

  // DEFENSIVE RANKINGS
  const totalYardsAllowed: IRanking[] = [];
  const totalPassingYardsAllowed: IRanking[] = [];
  const totalRushingYardsAllowed: IRanking[] = [];
  const totalPointsAllowed: IRanking[] = [];
  const totalSacks: IRanking[] = [];
  const totalDefensiveInterceptions: IRanking[] = [];
  const totalFumblesRecovered: IRanking[] = [];
  const totalSafeties: IRanking[] = [];
  const totalDefensiveTDs: IRanking[] = [];
  const totalScoringPassingTDAgainst: IRanking[] = [];
  const totalScoringRushingTDAgainst: IRanking[] = [];
  const totalScoringFGAgainst: IRanking[] = [];
  const totalScoringEPAgainst: IRanking[] = [];
  const totalScoring2PtAgainst: IRanking[] = [];
  const totalScoringTotalTDsAgainst: IRanking[] = [];

  // Calculations
  console.log(`Begin calculating weekly team ranks for ${weekID}`);
  // all available stats
  for (let i = 1; i <= NFL_TEAMS_TOTAL; i++) {
    const team: IEnhancedTeamStats | undefined = teamStatsForSelectedWeek.find((team) => team.teamID === String(i));
    if (team !== undefined && team.gameID !== 'BYE') {
      const teamID = team.teamID;
      const teamAbrv = team.team;
      const gameResult: Maybe<IGameStats> = gameStats.find((game) => game.gameID === team.gameID);

      totalYards.push({ teamID, teamName: teamAbrv, value: parseFloat(team.totalYards) });
      totalPlays.push({ teamID, teamName: teamAbrv, value: parseFloat(team.totalPlays) });
      totalYardsPerPlay.push({ teamID, teamName: teamAbrv, value: parseFloat(team.yardsPerPlay) });

      totalPassingYards.push({ teamID, teamName: teamAbrv, value: parseFloat(team.passingYards) });
      const [passCompletions, passAttempts] = team.passCompletionsAndAttempts.split('-');
      totalPassCompletions.push({ teamID, teamName: teamAbrv, value: parseFloat(passCompletions) });
      totalPassAttempts.push({ teamID, teamName: teamAbrv, value: parseFloat(passAttempts) });
      const attempts = parseFloat(passAttempts);
      const passPercentage =
        attempts !== 0 ? Math.round((parseFloat(passCompletions) / parseFloat(passAttempts)) * 10000) / 100 : 0;
      totalPassingPercentage.push({ teamID, teamName: teamAbrv, value: passPercentage });
      totalPassingYardsPerAttempt.push({ teamID, teamName: teamAbrv, value: parseFloat(team.yardsPerPass) });

      totalRushingYards.push({ teamID, teamName: teamAbrv, value: parseFloat(team.rushingYards) });
      totalRushingAttempts.push({ teamID, teamName: teamAbrv, value: parseFloat(team.rushingAttempts) });
      totalRushingYardsPerAttempt.push({ teamID, teamName: teamAbrv, value: parseFloat(team.yardsPerRush) });
      totalPoints.push({ teamID, teamName: teamAbrv, value: team.pointsScored ?? 0 });

      const [minutes, seconds] = team.possession.split(':');
      const totalSeconds = parseInt(minutes) * 60 + parseInt(seconds);
      totalTimeOfPossession.push({ teamID, teamName: teamAbrv, value: totalSeconds });
      totalInterceptionsThrown.push({ teamID, teamName: teamAbrv, value: parseInt(team.interceptionsThrown) });
      totalFumblesLost.push({ teamID, teamName: teamAbrv, value: parseInt(team.fumblesLost) });
      const [penalties, penaltyYards] = team.penalties.split('-');
      totalPenalties.push({ teamID, teamName: teamAbrv, value: parseInt(penalties) });
      totalPenaltyYards.push({ teamID, teamName: teamAbrv, value: parseInt(penaltyYards) });
      // DEFENSIVE RANKINGS
      totalYardsAllowed.push({ teamID, teamName: teamAbrv, value: parseInt(team.ydsAllowed) });
      totalPassingYardsAllowed.push({
        teamID,
        teamName: teamAbrv,
        value: parseInt(team.passingYardsAllowed ?? '0'),
      });
      totalRushingYardsAllowed.push({
        teamID,
        teamName: teamAbrv,
        value: parseInt(team.rushingYardsAllowed ?? '0'),
      });
      totalPointsAllowed.push({ teamID, teamName: teamAbrv, value: parseInt(team.ptsAllowed) });
      totalDefensiveInterceptions.push({
        teamID,
        teamName: teamAbrv,
        value: parseInt(team.defensiveInterceptions),
      });
      totalFumblesRecovered.push({ teamID, teamName: teamAbrv, value: parseInt(team.fumblesRecovered) });
      totalSacks.push({ teamID, teamName: teamAbrv, value: parseInt(team.sacks) });
      totalSafeties.push({ teamID, teamName: teamAbrv, value: parseInt(team.safeties) });
      totalDefensiveTDs.push({ teamID, teamName: teamAbrv, value: parseInt(team.defensiveOrSpecialTeamsTds) });

      const [sacks, yardsLost] = team.sacksAndYardsLost.split('-');
      totalSackedTimes.push({ teamID, teamName: teamAbrv, value: parseInt(sacks) });
      totalSackedYardsLost.push({ teamID, teamName: teamAbrv, value: parseInt(yardsLost) });
      totalDrives.push({ teamID, teamName: teamAbrv, value: parseInt(team.totalDrives) });

      totalFirstDowns.push({ teamID, teamName: teamAbrv, value: parseInt(team.firstDowns) });
      totalFirstDownsFromPenalties.push({ teamID, teamName: teamAbrv, value: parseInt(team.firstDownsFromPenalties) });
      totalFirstDownsFromRushing.push({ teamID, teamName: teamAbrv, value: parseInt(team.rushingFirstDowns) });
      totalFirstDownsFromPassing.push({ teamID, teamName: teamAbrv, value: parseInt(team.passingFirstDowns) });

      const [thirdDownConverted, thirdDownAttempts] = team.thirdDownEfficiency.split('-');
      totalThirdDownAttempts.push({ teamID, teamName: teamAbrv, value: parseInt(thirdDownAttempts) });
      totalThirdDownConverted.push({ teamID, teamName: teamAbrv, value: parseInt(thirdDownConverted) });
      const thirdDownEfficiency =
        parseFloat(thirdDownAttempts) !== 0
          ? Math.round((parseFloat(thirdDownConverted) / parseFloat(thirdDownAttempts)) * 10000) / 100
          : 0;
      totalThirdDownEfficiency.push({ teamID, teamName: teamAbrv, value: thirdDownEfficiency });

      const [fourthDownScored, fourthDownAttempted] = team.fourthDownEfficiency.split('-');
      totalFourthDownAttempts.push({ teamID, teamName: teamAbrv, value: parseInt(fourthDownAttempted) });
      totalFourthDownScored.push({ teamID, teamName: teamAbrv, value: parseInt(fourthDownScored) });
      const fourthDownEfficiency =
        parseFloat(fourthDownAttempted) !== 0
          ? Math.round((parseFloat(fourthDownScored) / parseFloat(fourthDownAttempted)) * 10000) / 100
          : 0;
      totalFourthDownEfficiency.push({ teamID, teamName: teamAbrv, value: fourthDownEfficiency });

      const [redZoneScored, redZoneAttempts] = team.redZoneScoredAndAttempted.split('-');
      totalRedZoneScored.push({ teamID, teamName: teamAbrv, value: parseInt(redZoneScored) });
      totalRedZoneAttempts.push({ teamID, teamName: teamAbrv, value: parseInt(redZoneAttempts) });
      const redZoneEfficiency =
        parseFloat(redZoneAttempts) !== 0
          ? Math.round((parseFloat(redZoneScored) / parseFloat(redZoneAttempts)) * 10000) / 100
          : 0;
      totalRedZoneEfficiency.push({ teamID, teamName: teamAbrv, value: redZoneEfficiency });

      // TDs scored
      if (gameResult) {
        const {
          scoringPassingTDAgainst,
          scoringRushingTDAgainst,
          scoringFGAgainst,
          scoringEPAgainst,
          scoring2PtAgainst,
          scoringTotalTDsAgainst,
          scoringPassingTDFor,
          scoringRushingTDFor,
          scoringFGFor,
          scoringEPFor,
          scoring2PtFor,
          scoringTotalTDsFor,
        } = extractGameScoringStats(gameResult.scoringPlays, teamID, teamAbrv);
        totalScoringPassingTDAgainst.push(scoringPassingTDAgainst);
        totalScoringRushingTDAgainst.push(scoringRushingTDAgainst);
        totalScoringFGAgainst.push(scoringFGAgainst);
        totalScoringEPAgainst.push(scoringEPAgainst);
        totalScoring2PtAgainst.push(scoring2PtAgainst);
        totalScoringTotalTDsAgainst.push({ teamID, teamName: teamAbrv, value: scoringTotalTDsAgainst.value });
        totalScoringPassingTDFor.push(scoringPassingTDFor);
        totalScoringRushingTDFor.push(scoringRushingTDFor);
        totalScoringFGFor.push(scoringFGFor);
        totalScoringEPFor.push(scoringEPFor);
        totalScoring2PtFor.push(scoring2PtFor);
        totalScoringTotalTDsFor.push({ teamID, teamName: teamAbrv, value: scoringTotalTDsFor.value });
      }
    }
  }

  // #region Sort Rankings
  totalYards.sort(sortTeamsByValue).reverse();
  totalPlays.sort(sortTeamsByValue).reverse();
  totalYardsPerPlay.sort(sortTeamsByValue).reverse();
  totalPassingYards.sort(sortTeamsByValue).reverse();
  totalPassCompletions.sort(sortTeamsByValue).reverse();
  totalPassAttempts.sort(sortTeamsByValue).reverse();
  totalPassingPercentage.sort(sortTeamsByValue).reverse();
  totalPassingYardsPerAttempt.sort(sortTeamsByValue).reverse();
  totalPassAttempts.sort(sortTeamsByValue).reverse();
  totalRushingYards.sort(sortTeamsByValue).reverse();
  totalRushingAttempts.sort(sortTeamsByValue).reverse();
  totalRushingYardsPerAttempt.sort(sortTeamsByValue).reverse();
  totalPoints.sort(sortTeamsByValue).reverse();
  totalInterceptionsThrown.sort(sortTeamsByValue);
  totalFumblesLost.sort(sortTeamsByValue);
  totalTimeOfPossession.sort(sortTeamsByValue).reverse();
  totalPenalties.sort(sortTeamsByValue);
  totalPenaltyYards.sort(sortTeamsByValue);
  totalScoringPassingTDFor.sort(sortTeamsByValue).reverse();
  totalScoringRushingTDFor.sort(sortTeamsByValue).reverse();
  totalScoringFGFor.sort(sortTeamsByValue).reverse();
  totalScoringEPFor.sort(sortTeamsByValue).reverse();
  totalScoring2PtFor.sort(sortTeamsByValue).reverse();
  totalScoringTotalTDsFor.sort(sortTeamsByValue).reverse();

  totalSackedTimes.sort(sortTeamsByValue);
  totalSackedYardsLost.sort(sortTeamsByValue);
  totalDrives.sort(sortTeamsByValue).reverse();
  totalThirdDownAttempts.sort(sortTeamsByValue).reverse();
  totalThirdDownConverted.sort(sortTeamsByValue).reverse();
  totalRedZoneScored.sort(sortTeamsByValue).reverse();
  totalRedZoneAttempts.sort(sortTeamsByValue).reverse();
  totalFirstDowns.sort(sortTeamsByValue).reverse();
  totalFirstDownsFromPenalties.sort(sortTeamsByValue).reverse();
  totalFirstDownsFromRushing.sort(sortTeamsByValue).reverse();
  totalFirstDownsFromPassing.sort(sortTeamsByValue).reverse();
  totalFourthDownAttempts.sort(sortTeamsByValue).reverse();
  totalFourthDownScored.sort(sortTeamsByValue).reverse();
  totalThirdDownEfficiency.sort(sortTeamsByValue).reverse();
  totalRedZoneEfficiency.sort(sortTeamsByValue).reverse();
  totalFourthDownEfficiency.sort(sortTeamsByValue).reverse();

  // DST
  totalYardsAllowed.sort(sortTeamsByValue);
  totalPassingYardsAllowed.sort(sortTeamsByValue);
  totalRushingYardsAllowed.sort(sortTeamsByValue);
  totalPointsAllowed.sort(sortTeamsByValue);
  totalSacks.sort(sortTeamsByValue).reverse();
  totalDefensiveInterceptions.sort(sortTeamsByValue).reverse();
  totalFumblesRecovered.sort(sortTeamsByValue).reverse();
  totalSafeties.sort(sortTeamsByValue).reverse();
  totalDefensiveTDs.sort(sortTeamsByValue).reverse();
  totalScoringPassingTDAgainst.sort(sortTeamsByValue);
  totalScoringRushingTDAgainst.sort(sortTeamsByValue);
  totalScoringFGAgainst.sort(sortTeamsByValue);
  totalScoringEPAgainst.sort(sortTeamsByValue);
  totalScoring2PtAgainst.sort(sortTeamsByValue);
  totalScoringTotalTDsAgainst.sort(sortTeamsByValue);
  // #endregion

  const getCategory = (title: CategoryNameT, ranking: IRanking[]): IWeekRank => ({
    adjustedRanking: [],
    rankCategory: title,
    ranking,
    weekID: weekID,
  });

  const weeklyRanking: IRankingCategories = {
    totalDefensiveInterceptions: getCategory(CategoryName.DefensiveInterceptions, totalDefensiveInterceptions),
    totalDefensiveOrSpecialTeamsTds: getCategory(CategoryName.DefensiveTDs, totalDefensiveTDs),
    totalForcedFumbles: getCategory(CategoryName.FumblesRecovered, totalFumblesRecovered),
    totalFumblesLost: getCategory(CategoryName.FumblesLost, totalFumblesLost),
    totalInterceptionsThrown: getCategory(CategoryName.InterceptionsThrown, totalInterceptionsThrown),
    totalPassAttempts: getCategory(CategoryName.PassAttempts, totalPassAttempts),
    totalPassCompletions: getCategory(CategoryName.PassCompletions, totalPassCompletions),
    totalPassingPercentage: getCategory(CategoryName.PassingPercentage, totalPassingPercentage),
    totalPassingYards: getCategory(CategoryName.PassingYards, totalPassingYards),
    totalPassingYardsAllowed: getCategory(CategoryName.PassingYardsAllowed, totalPassingYardsAllowed),
    totalPassingYardsPerAttempt: getCategory(CategoryName.PassingYardsPerAttempt, totalPassingYardsPerAttempt),
    totalPenalties: getCategory(CategoryName.Penalties, totalPenalties),
    totalPenaltyYards: getCategory(CategoryName.PenaltyYards, totalPenaltyYards),
    totalPlays: getCategory(CategoryName.TotalPlays, totalPlays),
    totalPointsAllowed: getCategory(CategoryName.PointsAllowed, totalPointsAllowed),
    totalPointsScored: getCategory(CategoryName.PointsScored, totalPoints),
    totalRushingAttempts: getCategory(CategoryName.RushingAttempts, totalRushingAttempts),
    totalRushingYards: getCategory(CategoryName.RushingYards, totalRushingYards),
    totalRushingYardsAllowed: getCategory(CategoryName.RushingYardsAllowed, totalRushingYardsAllowed),
    totalRushingYardsPerAttempt: getCategory(CategoryName.RushingYardsPerAttempt, totalRushingYardsPerAttempt),
    totalSacks: getCategory(CategoryName.Sacks, totalSacks),
    totalSafeties: getCategory(CategoryName.Safeties, totalSafeties),
    totalScoring2PtAgainst: getCategory(CategoryName.Allowed2ptConversions, totalScoring2PtAgainst),
    totalScoring2PtFor: getCategory(CategoryName.Scored2ptConversions, totalScoring2PtFor),
    totalScoringEPAgainst: getCategory(CategoryName.AllowedExtraPoints, totalScoringEPAgainst),
    totalScoringEPFor: getCategory(CategoryName.ScoringEPFor, totalScoringEPFor),
    totalScoringFGAgainst: getCategory(CategoryName.AllowedFGs, totalScoringFGAgainst),
    totalScoringFGFor: getCategory(CategoryName.ScoredFGs, totalScoringFGFor),
    totalScoringPassingTDAgainst: getCategory(CategoryName.AllowedPassingTDs, totalScoringPassingTDAgainst),
    totalScoringPassingTDFor: getCategory(CategoryName.ScoredPassingTDs, totalScoringPassingTDFor),
    totalScoringRushingTDAgainst: getCategory(CategoryName.AllowedRushingTDs, totalScoringRushingTDAgainst),
    totalScoringRushingTDFor: getCategory(CategoryName.ScoredRushingTDs, totalScoringRushingTDFor),
    totalScoringTotalTDAgainst: getCategory(CategoryName.TotalTDsAllowed, totalScoringTotalTDsAgainst),
    totalScoringTotalTDFor: getCategory(CategoryName.TotalTDsScored, totalScoringTotalTDsFor),
    totalTimeOfPossession: getCategory(CategoryName.TimeOfPossession, totalTimeOfPossession),
    totalYards: getCategory(CategoryName.TotalYards, totalYards),
    totalYardsAllowed: getCategory(CategoryName.TotalYardsAllowed, totalYardsAllowed),
    totalYardsPerPlay: getCategory(CategoryName.TotalYardsPerPlay, totalYardsPerPlay),
    totalSackedTimes: getCategory(CategoryName.TotalSackedTimes, totalSackedTimes),
    totalSackedYardsLost: getCategory(CategoryName.TotalSackedYardsLost, totalSackedYardsLost),
    totalDrives: getCategory(CategoryName.TotalDrives, totalDrives),
    totalThirdDownAttempts: getCategory(CategoryName.ThirdDownAttempts, totalThirdDownAttempts),
    totalThirdDownConverted: getCategory(CategoryName.ThirdDownConverted, totalThirdDownConverted),
    totalThirdDownEfficiency: getCategory(CategoryName.ThirdDownEfficiency, totalThirdDownEfficiency),
    totalRedZoneScored: getCategory(CategoryName.RedZoneScored, totalRedZoneScored),
    totalRedZoneAttempts: getCategory(CategoryName.RedZoneAttempts, totalRedZoneAttempts),
    totalRedZoneEfficiency: getCategory(CategoryName.RedZoneEfficiency, totalRedZoneEfficiency),
    totalFirstDowns: getCategory(CategoryName.TotalFirstDowns, totalFirstDowns),
    totalFirstDownsFromPenalties: getCategory(CategoryName.FirstDownsFromPenalties, totalFirstDownsFromPenalties),
    totalFirstDownsFromRushing: getCategory(CategoryName.FirstDownsFromRushing, totalFirstDownsFromRushing),
    totalFirstDownsFromPassing: getCategory(CategoryName.FirstDownsFromPassing, totalFirstDownsFromPassing),
    totalFourthDownAttempts: getCategory(CategoryName.FourthDownAttempts, totalFourthDownAttempts),
    totalFourthDownScored: getCategory(CategoryName.FourthDownScored, totalFourthDownScored),
    totalFourthDownEfficiency: getCategory(CategoryName.FourthDownEfficiency, totalFourthDownEfficiency),
  };

  return weeklyRanking;
};
// #endregion

// #region TEAM SEASON SCHEDULE HELPERS *************************************************
/**
 * Converts a season schedule list into a map of teams and their season schedule formatted as `UID`
 * e.g. `2024-03-11-Week3-11-DET-20240922_DET@ARI`
 *
 * @param {IGame[]} seasonSchedule season schedule
 * @param {ByeWeekID[]} seasonByeWeeks list of all teams on bye weeks for the season
 *
 * @return {AllSeasonScheduleByTeamType} a map of all teams and their season schedule
 */
export const parseTeamsSeasonScheduler = (
  seasonSchedule: IMatchup[],
  seasonByeWeeks: ByeWeekID[]
): AllSeasonScheduleByTeamType => {
  const allTeamsOrderedUID: AllSeasonScheduleByTeamType = {};

  seasonSchedule.forEach((game: IMatchup) => {
    const gameID: GameID = game.gameID;
    // const [awayTeam, homeTeam] = gameID.split('_')[1].split('@');
    const homeTeamID = game.teamIDHome;
    const awayTeamID = game.teamIDAway;
    const homeTeamKey = `${homeTeamID.padStart(4, '0')}`;
    const awayTeamKey = `${awayTeamID.padStart(4, '0')}`;
    const weekID: WeekID = game.gameWeek.replace(' ', '') as WeekID;

    if (!allTeamsOrderedUID[homeTeamKey]) allTeamsOrderedUID[homeTeamKey] = [];
    if (!allTeamsOrderedUID[awayTeamKey]) allTeamsOrderedUID[awayTeamKey] = [];

    allTeamsOrderedUID[homeTeamKey].push(toUID(weekID, awayTeamID, gameID));
    allTeamsOrderedUID[awayTeamKey].push(toUID(weekID, homeTeamID, gameID));
  });

  seasonByeWeeks.forEach((byeWeekID) => {
    const { teamID, seasonID, weekID } = breakByeWeekID(byeWeekID);
    const keyID = teamID.padStart(4, '0');
    const uID = toUID(weekID, teamID, 'BYE', seasonID);
    allTeamsOrderedUID[keyID].push(uID);
    allTeamsOrderedUID[keyID].sort();
  });

  return allTeamsOrderedUID;
};

/**
 * Returns list of all team ids 1-32 as numbers.
 *
 * @return {Array<number>} list of IDs from 1 to 32
 */
const getAllTeamIDs = () => Array.from({ length: 32 }, (_, i) => i + 1);

/**
 * Given a list of teams playing in a given `weekNum,`
 * returns all missing TeamIDs as those are the teams on byeweek.
 *
 * @param {number} weekNum - season week id as `number`
 * @param teamIDs - `teamID`s playing in a given week
 *
 * @return {ByeWeekID[]} teams on bye for a given week in form `ByeWeekID[]`
 */
const generateTeamsOnByeWeek = (seasonID: SeasonID, weekNum: number, teamIDs: TeamID[]): ByeWeekID[] => {
  const ids = getAllTeamIDs();
  let byeWeekTeams: ByeWeekID[] = [];
  const byeIds = ids
    .filter((id) => !teamIDs.includes(String(id)))
    .map((i) => toByeWeekID(toWeekID(weekNum), `${i}`, seasonID));
  byeWeekTeams = [...byeWeekTeams, ...byeIds];

  return byeWeekTeams;
};

/**
 * Given a season schedule list returns two lists back,
 * `gameIds` map in form `{"2025-01": ["20250904_DAL@PHI", "20250905_KC@LAC"], ...}` and
 * `byeWeeks` list in form `byeWeeks: ["2025-05-06-CHI", "2025-05-12-GB", ...]`
 *
 * @param schedule {IMatchup[]} list of the games for a given season
 */
export const createGameIdsByWeekAndByeWeeksBySeasonSchedule = (schedule: IMatchup[]) => {
  // generate a map of weekly games, eg `{"2024-01": [20240905_BAL@KC, ...],...}`
  const gameIDMap: { [key: SeasonWeekID]: GameID[] } = {};
  // add all teams on bye week, eg:  byeWeeks: [`2024-05-11-DET`, ...]
  const byeWeeks: TeamID[][] = [['0']];
  //
  const seasonID = schedule[0].season as SeasonID;

  schedule.forEach((match: IMatchup) => {
    const matchSeasonWeekID = match.seasonWeekID;
    if (!Array.isArray(gameIDMap[matchSeasonWeekID])) gameIDMap[matchSeasonWeekID] = [];
    gameIDMap[matchSeasonWeekID] = [...gameIDMap[matchSeasonWeekID], match.gameID];
    const weekID = match.gameWeek.replace('Week ', '');
    if (!Array.isArray(byeWeeks[parseInt(weekID)])) byeWeeks[parseInt(weekID)] = [];
    byeWeeks[parseInt(weekID)] = [...byeWeeks[parseInt(weekID)], match.teamIDAway, match.teamIDHome];
  });

  let byeWeekTeams: ByeWeekID[] = [];
  byeWeeks.forEach((row, index) => {
    if (index !== 0) {
      if (row.length < NFL_TEAMS_TOTAL) {
        const weekRow = row.slice(0).sort(sortByTeamID);
        byeWeekTeams = [...byeWeekTeams, ...generateTeamsOnByeWeek(seasonID, index, weekRow)];
      }
    }
  });

  return { gameIDs: gameIDMap, byeWeeks: byeWeekTeams.sort() };
};
// #endregion
/**
'Defensive Interceptions'
'Defensive TDs'
'Fumbles Recovered'
'Fumbles Lost'
'Interceptions Thrown'
'Pass Attempts'
'Pass Completions'
'Passing Percentage'
'Passing Yards'
'Passing Yards Allowed'
'Passing Yards Per Attempt'
'Penalties'
'Penalty Yards'
'Total Plays'
'Points Allowed'
'Points Scored'
'Rushing Attempts'
'Rushing Yards'
'Rushing Yards Allowed'
'Rushing Yards Per Attempt'
'Sacks'
'Safeties'
'Allowed 2pt Conversions'
'Scored 2pt Conversions'
'Allowed Extra Points'
'Scored Extra Points'
'Allowed FGs'
'Scored FGs'
'Allowed Passing TDs'
'Scored Passing TDs'
'Allowed Rushing TDs'
'Scored Rushing TDs'
'Total TDs Allowed'
'Total TDs Scored'
'Time of Possession'
'Total Yards'
'Total Yards Allowed'
'Total Yards Per Play'
 */

/**
 * Creates an empty/placeholder team rank object with default values.
 *
 * @description
 * Generates a complete ITeamWeekRank structure initialized with zeros and empty
 * values. Used for teams on bye weeks or when rank data is not yet available.
 *
 * @param {TeamID} teamID - The team identifier
 * @param {string} teamAbrv - The team abbreviation
 * @param {WeekID} weekID - The week identifier
 *
 * @return {ITeamWeekRank} - Empty rank object with all values set to 0 or empty
 *
 * @example
 * const emptyRank = getEmptyTeamRank('11', 'DET', 'Week5');
 * // Returns rank object with rank: 0, value: 0, formatUnit: 'DNP'
 */
export const getEmptyTeamRank = (teamID: TeamID, teamAbrv: string, weekID: WeekID): ITeamWeekRank => {
  const emptyBestWorst: IRanking = {
    teamName: '',
    teamID: '',
    value: 0,
    isRankTied: false,
  };

  const emptyRank: ITeamWeekRank = {
    rankCategory: CategoryName.ScoringEPFor,
    teamID: teamID,
    teamName: teamAbrv,
    value: 0,
    rank: 0,
    week: weekID as GameWeek,
    isRankTied: false,
    bestTeam: emptyBestWorst,
    worstTeam: emptyBestWorst,
    formatUnit: 'DNP',
    highOrLow: 'High',
    isByeWeek: false,
  };

  return emptyRank;
};

/**
 * Get a sorted list of team abbreviation IDs from the weekly schedule
 *
 * This function extracts all team abbreviation IDs from the given weekly schedule,
 * including both home and away teams, and returns a sorted list of these IDs.
 *
 * @param {IGame[]} weekSchedule - The weekly games schedule
 *
 * @return {TeamAbrvID[]} - Sorted list of team abbreviation IDs
 */
export const getListOfTeamAbrvIDs = (weekSchedule: IGame[]): TeamAbrvID[] => {
  const listOfTeamAbrvIDs: TeamAbrvID[] = [];
  weekSchedule.forEach((game: IGame) => {
    const homeTeamAbrvID = toTeamAbrvID(game.teamIDHome);
    const awayTeamAbrvID = toTeamAbrvID(game.teamIDAway);
    listOfTeamAbrvIDs.push(homeTeamAbrvID, awayTeamAbrvID);
  });
  listOfTeamAbrvIDs.sort();
  return listOfTeamAbrvIDs;
};

/**
 * Determines if two teams are rivals based on team context and rivalry maps.
 *
 * @description
 * Checks both team context rivalry lists and the local TeamRivalries map to
 * determine if teams have a rivalry relationship. Used for game context enrichment.
 *
 * @param {TeamBasicContext} teamA - First team context object
 * @param {TeamBasicContext} teamB - Second team context object
 *
 * @return {boolean} - True if teams are rivals, false otherwise
 *
 * @example
 * areRivalTeams(packers, bears);  // true (historic NFC North rivalry)
 */
// export const areRivalTeams = (teamA: TeamBasicContext, teamB: TeamBasicContext): boolean => {
//   const aTeamAbrvID = toTeamAbrvID(teamA.id);
//   const bTeamAbrvID = toTeamAbrvID(teamB.id);
//   return (
//     teamA.rivalries.includes(bTeamAbrvID) ||
//     teamB.rivalries.includes(aTeamAbrvID) ||
//     areRivalTeamsFromLocalMap(aTeamAbrvID, bTeamAbrvID)
//   );
// };

/**
 * Checks if two teams are rivals using the local TeamRivalries constant.
 *
 * @param {TeamAbrvID} teamA - First team abbreviation ID
 * @param {TeamAbrvID} teamB - Second team abbreviation ID
 *
 * @return {boolean} - True if both teams list each other as rivals
 *
 * @example
 * areRivalTeamsFromLocalMap('0011-DET', '0012-GB');  // true (division rivals)
 */
export const areRivalTeamsFromLocalMap = (teamA: TeamAbrvID, teamB: TeamAbrvID): boolean => {
  return TeamRivalries[teamA].includes(teamB) && TeamRivalries[teamB].includes(teamA);
};
